import { apiRequest } from "@/api/client";
import type {
  App,
  AuditEvent,
  DebugWindow,
  HostContainers,
  HostCorrelation,
  HostDisks,
  HostOverview,
  HostProcesses,
  IngestionHealth,
  Incident,
  IncidentListInput,
  LatestLogs,
  LogSearchInput,
  Page,
  SignalFamilies,
  Station
} from "@/api/types";

export const adminApi = {
  apps: () => apiRequest<Page<App>>("/admin/v1/apps"),
  ingestionHealth: () => apiRequest<IngestionHealth>("/admin/v1/ingestion/health?sinceHours=24&limit=20"),
  auditEvents: () => apiRequest<Page<AuditEvent>>("/admin/v1/audit/events?limit=50"),
  incidents: (input: IncidentListInput) => apiRequest<Page<Incident>>(`/admin/v1/incidents?${incidentListParams(input)}`),
  clientStations: (clientId: number, limit = 50) => apiRequest<Page<Station>>(`/admin/v1/clients/${clientId}/stations?limit=${limit}`),
  clientIncidents: (clientId: number, status = "open", limit = 50) =>
    apiRequest<Page<Incident>>(`/admin/v1/clients/${clientId}/incidents?status=${status}&limit=${limit}`),
  clientLatestErrors: (clientId: number, limit = 25) =>
    apiRequest<LatestLogs>(`/admin/v1/clients/${clientId}/latest-errors?limit=${limit}`),
  logsSearch: (input: LogSearchInput) =>
    apiRequest<LatestLogs>("/admin/v1/logs/search", {
      method: "POST",
      body: JSON.stringify(input)
    }),
  hostOverview: (clientId: number, hostId: string) =>
    apiRequest<HostOverview>(`/admin/v1/clients/${clientId}/hosts/${encodeURIComponent(hostId)}/overview`),
  hostSignalFamilies: (clientId: number, hostId: string) =>
    apiRequest<SignalFamilies>(`/admin/v1/clients/${clientId}/hosts/${encodeURIComponent(hostId)}/signal-families`),
  hostDisks: (clientId: number, hostId: string) =>
    apiRequest<HostDisks>(`/admin/v1/clients/${clientId}/hosts/${encodeURIComponent(hostId)}/disks`),
  hostProcesses: (clientId: number, hostId: string) =>
    apiRequest<HostProcesses>(`/admin/v1/clients/${clientId}/hosts/${encodeURIComponent(hostId)}/processes`),
  hostContainers: (clientId: number, hostId: string) =>
    apiRequest<HostContainers>(`/admin/v1/clients/${clientId}/hosts/${encodeURIComponent(hostId)}/containers`),
  hostCorrelation: (clientId: number, hostId: string, limit = 10) =>
    apiRequest<HostCorrelation>(`/admin/v1/clients/${clientId}/hosts/${encodeURIComponent(hostId)}/correlation?limit=${limit}`),
  stationIncidents: (stationId: string, status = "open", limit = 50) =>
    apiRequest<Page<Incident>>(`/admin/v1/stations/${stationId}/incidents?status=${status}&limit=${limit}`),
  incident: (incidentId: string) => apiRequest<Incident>(`/admin/v1/incidents/${incidentId}`),
  acknowledgeIncident: (incidentId: string) =>
    apiRequest<Incident>(`/admin/v1/incidents/${incidentId}/acknowledge`, {
      method: "POST",
      body: JSON.stringify({})
    }),
  resolveIncident: (incidentId: string, note: string) =>
    apiRequest<Incident>(`/admin/v1/incidents/${incidentId}/resolve`, {
      method: "POST",
      body: JSON.stringify({ note })
    }),
  reopenIncident: (incidentId: string) =>
    apiRequest<Incident>(`/admin/v1/incidents/${incidentId}/reopen`, {
      method: "POST",
      body: JSON.stringify({})
    }),
  commentIncident: (incidentId: string, body: string) =>
    apiRequest(`/admin/v1/incidents/${incidentId}/comments`, {
      method: "POST",
      body: JSON.stringify({ body })
    }),
  createDebugWindow: (stationId: string, durationMinutes: number, reason: string) =>
    apiRequest<DebugWindow>(`/admin/v1/stations/${stationId}/debug-windows`, {
      method: "POST",
      body: JSON.stringify({ durationMinutes, reason })
    }),
  revokeDebugWindow: (stationId: string) =>
    apiRequest<DebugWindow>(`/admin/v1/stations/${stationId}/debug-windows/current`, {
      method: "DELETE"
  })
};

function incidentListParams(input: IncidentListInput) {
  const params = new URLSearchParams();
  if (input.clientId !== undefined) {
    params.set("clientId", String(input.clientId));
  }
  if (input.status) {
    params.set("status", input.status);
  }
  if (input.severity) {
    params.set("severity", input.severity);
  }
  if (input.appId) {
    params.set("appId", input.appId);
  }
  if (input.ruleId) {
    params.set("ruleId", input.ruleId);
  }
  if (input.limit !== undefined) {
    params.set("limit", String(input.limit));
  }
  return params.toString();
}
